import React, { useEffect, useState } from "react";
import { Menu, MenuButton, MenuDivider, MenuItem } from "@szhsin/react-menu";
import { useDispatch, useSelector } from "react-redux";
import { fetchActions, fetchSettings, toggleCorporate } from "./redux/usersSagas";
import { usersApi } from "./api";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import { Add } from "./smartComponents/Add";
import { Edit } from "./smartComponents/Edit";
import { Delete } from "./smartComponents/Delete";
import { usePopup } from "./hooks/usePopup";
import FilterUsers from "./smartComponents/FilterUsers";
import { Duplicate } from "./smartComponents/Duplicate";
import { Mirror } from "./smartComponents/Mirror";
import { ResetPassword } from "./smartComponents/ResetPassword";
import { CreateCustomPermissionGroup } from "./smartComponents/CreateCustomPermissionsGroup";
import { LoginAs } from "./smartComponents/LoginAs";
import { showToast } from "../../../utils/showToast";
import { getBaseUrlParams } from "../../../utils/getBaseUrlParams";
import { Trash, Edit as EditIcon, IconDots } from "../../../../assets/images/svg";
import "./users.scss";

const requestMenuValues = {
  resetPasswordInstructions: usersApi.sendResetPasswordInstructions,
  welcomeEmail: usersApi.sendWelcomeEmail,
  fitlabMonthlyEmail: usersApi.sendFitlabMonthlyEmail
};

const makeRequest = async (endpoint, id) => {
  try {
    const { organization_id } = getBaseUrlParams();
    let res = await endpoint({ organization_id, id: id.toString() });
    showToast(res.data.message);
  } catch (err) {
    showToast(err.response.error);
    console.log(err);
  }
};

export function UsersModule({ params }) {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.users);
  const canCreateUser = useSelector((state) => state.users.commonSettings.canCreateUser);

  const isLoading = useSelector((state) => state.users.isLoading);
  const areUsersLoading = useSelector((state) => state.users.areUsersLoading);
  const actionsLoading = useSelector((state) => state.users.actionsLoading);

  const currentUserActions = useSelector((state) => state.users.currentUserActions);

  const [selectedUser, setSelectedUser] = useState("");

  const { popup, openPopup, closePopup } = usePopup();

  const handlePopup = (item, type) => {
    if (requestMenuValues[type]) {
      makeRequest(requestMenuValues[type], item.id);
      return;
    }

    setSelectedUser(item);

    openPopup(type);
  };

  useEffect(() => {
    dispatch(fetchSettings(params));
  }, []);

  if (isLoading)
    return (
      <div className="light">
        <LoadingAnimation uiLoader />
      </div>
    );

  const activeTabClassName = "tabs__tab--active";

  const onMenuClick = ({ value, syntheticEvent }, item) => {
    syntheticEvent.preventDefault();

    handlePopup(item, value);
  };

  const menu = (name) => (
    <>
      {currentUserActions.canDuplicate && (
        <>
          <MenuItem value="duplicate">
            <span>Duplicate</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canMirror && (
        <>
          <MenuItem value="mirror">
            <span>Mirror</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      <MenuItem value="resetPassword">
        <span>Reset Password</span>
      </MenuItem>
      <MenuDivider />

      {currentUserActions.canSendResetPasswordInstructions && (
        <>
          <MenuItem value="resetPasswordInstructions">
            <span>Send Reset Password Instruction</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canSendWelcomeEmail && (
        <>
          <MenuItem value="welcomeEmail">
            <span>Send Welcome Email</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canSendFitlabMonthlyEmail && (
        <>
          <MenuItem value="fitlabMonthlyEmail">
            <span>Send Fitlab Monthly Email</span>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {currentUserActions.canSetCustomPermissions && (
        <>
          <MenuItem value="customPermissionGroup">
            <span>Save {name}&apos;s Settings as New Role </span>
          </MenuItem>
          <MenuDivider />
        </>
      )}
    </>
  );

  const renderMenu = (item) => {
    return (
      <Menu
        menuButton={
          <MenuButton>
            <IconDots />
          </MenuButton>
        }
        onMenuChange={({ open }) => {
          if (open) {
            dispatch(fetchActions(item.id));
          }
        }}
        onItemClick={(eventObj) => onMenuClick(eventObj, item)}
        direction="left"
        align="start"
        transition
      >
        {actionsLoading ? <LoadingAnimation uiLoader /> : menu(item.name)}
      </Menu>
    );
  };

  const onSetCorporate = ({ e, ...rest }) => {
    if (!e.target.className.includes(activeTabClassName)) {
      dispatch(toggleCorporate(rest));
    }
  };

  return (
    <div id="users">
      <div className="users">
        {canCreateUser && (
          <div className="users__add">
            <button onClick={() => openPopup("add")} className="button button--primary">
              Add New User
            </button>
          </div>
        )}

        <div className="users__top">
          <FilterUsers />
        </div>

        <div className="users__content">
          <ul className="group">
            {areUsersLoading ? (
              <LoadingAnimation />
            ) : (
              users &&
              users.map((user, index) => {
                const {
                  id,
                  firstName,
                  lastName,
                  isCorporate,
                  userActions: {
                    canDestroy,
                    canEditUser,
                    canLoginAs,
                    canLoginAsText,
                    canSetAsCorporate,
                    canUpdatePermissions
                  }
                } = user;
                user.name = lastName + ", " + firstName;

                return (
                  <li color="dark" className="group__item" key={id}>
                    <div className="group__el text">
                      {lastName}, {firstName}{" "}
                      {canLoginAsText && <span className="no-permissions">-{canLoginAsText}</span>}
                    </div>

                    {canSetAsCorporate && (
                      <div className="tabs">
                        <div
                          className="tabs__list"
                          onClick={(e) => onSetCorporate({ e, isCorporate, id, index })}
                        >
                          <button className={`tabs__tab ${!isCorporate ? activeTabClassName : ""}`}>
                            Owner/Staff
                          </button>
                          <button className={`tabs__tab ${isCorporate ? activeTabClassName : ""}`}>
                            Corporate User
                          </button>
                        </div>
                      </div>
                    )}

                    {canLoginAs && (
                      <div className="group__el login-as">
                        <button
                          className="button button--secondary"
                          onClick={() => handlePopup(user, "loginAs")}
                        >
                          Login As
                        </button>
                      </div>
                    )}

                    {/* Menu */}
                    <div className="group__el users__menu">{renderMenu(user)}</div>

                    {(canEditUser || canUpdatePermissions) && (
                      <div className="group__el edit">
                        <span
                          className="group__icon"
                          onClick={() => handlePopup(user, "edit")}
                          title="Edit"
                        >
                          <EditIcon />
                        </span>
                      </div>
                    )}

                    {canDestroy && (
                      <div className="group__el delete">
                        <span
                          className="group__icon"
                          onClick={() => handlePopup(user, "delete")}
                          title="Delete"
                        >
                          <Trash color="#FF0000" />
                        </span>
                      </div>
                    )}
                  </li>
                );
              })
            )}
            {users && users.length === 0 && <p className="error error--medium">No users found</p>}
          </ul>
        </div>

        {popup.add && <Add isOpen={popup.add} onClose={closePopup("add")} />}

        {popup.edit && (
          <Edit isOpen={popup.edit} onClose={closePopup("edit")} selected={selectedUser} />
        )}

        {popup.duplicate && (
          <Duplicate
            isOpen={popup.duplicate}
            onClose={closePopup("duplicate")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.mirror && (
          <Mirror
            isOpen={popup.mirror}
            onClose={closePopup("mirror")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.customPermissionGroup && (
          <CreateCustomPermissionGroup
            isOpen={popup.customPermissionGroup}
            onClose={closePopup("customPermissionGroup")}
            selected={{ id: selectedUser.id }}
          />
        )}

        {popup.resetPassword && (
          <ResetPassword
            isOpen={popup.resetPassword}
            onClose={closePopup("resetPassword")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.delete && (
          <Delete
            isOpen={popup.delete}
            onClose={closePopup("delete")}
            selected={{ id: selectedUser.id, name: selectedUser.name }}
          />
        )}

        {popup.loginAs && (
          <LoginAs
            isOpen={popup.loginAs}
            onClose={closePopup("loginAs")}
            selected={{ id: selectedUser.id }}
          />
        )}
      </div>
    </div>
  );
}
